/**
 * **抽牌动画** —— 结束回合抽到的那张牌，从牌堆顶上"翻"到画面中央，
 * 再落进手牌里**它自己的那一格**。
 *
 * 评审给的整段动作：
 *
 * > 按回合结束时，在抽牌堆顶部位置生成一张牌背，播放两个动画，同时开始同时
 * > 结束。其中一个是之前实现过的将牌背揭露为牌面的动画，另一个是从抽牌堆
 * > 飞向屏幕中心的动画。完成动画后稍微停顿，若造成濒死则停留在屏幕中央，
 * > 若不是则飞入手牌。
 *
 * ## 三段
 *
 * ```
 *   ① 飞 + 翻（同一段，同时开始同时结束）   距离决定时长
 *      位置：抽牌堆顶 → 屏幕正中（**直线**，ease-out）
 *      牌面：牌背 → 转到 90° 换面 → 牌面
 *   ② 停顿                                 HOLD_MS
 *   ③ 落地
 *      ├ 造成濒死 → 停在中点，等濒死结束再淡出
 *      └ 其他     → 落进手牌那一格           距离决定时长
 * ```
 *
 * ## 时长：**直线移动，时长随距离线性增加，速度曲线是 ease-out**
 *
 * 评审：
 *
 * > 重做卡牌移动时的逻辑，改为 Ease-out 速度曲线的直线移动，动画总时长根据
 * > 移动距离线性增加。
 *
 * 所以时长不再是写死的两三个毫秒数，而是 `travelMs(from, to)`：
 *
 * ```
 *   时长 = 距离 × TRAVEL_MS_PER_PX   （夹在 [MIN, MAX] 之间）
 *   位置 = 起点 +（终点 − 起点）× easeOut(t)
 * ```
 *
 * ⚠️ 那两个上下限不是装饰：① 的飞行时长同时是**翻牌**的时长，而翻牌两半
 * 各占一半 —— 上下限正好把每一半压在 `visual-design.md` §6 的 90–220ms 里
 * （`MIN = 2 × DURATION.fast`、`MAX = 2 × DURATION.slow`）。改这两个数之前
 * 先看一眼那一条。
 *
 * ## 落点是**手牌给的**，不是这里算的
 *
 * 评审：
 *
 * > 牌飞入手牌只是垂直向下移动，并没有移动到其准确位置。飞入手牌的效果应当是
 * > 新牌和手牌的代码协同的。手牌代码告诉新牌目的地坐标，旋转角度，尺寸等所有
 * > 参数，然后自身有一个加牌时的动画令其他手牌让出一个空位；新牌到位后告诉
 * > 手牌代码，令其渲染为添加完牌后的状态并删除新牌。
 *
 * 所以这里的 `to` 是 `Hand.reserveArrival()` 交给我们的一个**完整姿态**
 * （`ArrivalPose`：坐标 + 旋转 + 尺寸）。手牌那边同时把扇形重新排成
 * "多一张"的样子，其他牌自己让开一个空位；牌一到位，调用方就
 * `commitArrival()` 把手牌切成"加完牌"的状态，这一层随即被销毁。
 *
 * ⚠️ 飞行途中只有**平面**的那几个参数（坐标 / 旋转 / 尺寸）能贴上去：
 * 手牌那种假透视（`pitch` / `perspective`）需要 `PerspectiveMesh`，而翻牌
 * 用的是"两张图 + `scale.x`"。所以最后一刻是**换手**：手牌把真正的
 * `CardView` 画在那一格上，飞行的那张同时删掉 —— 落点因此是精确的。
 *
 * ## 濒死那一段为什么是"淡出"而不是"飞走"
 *
 * "停留在屏幕中央"之后，那张牌的去向取决于玩家拆不拆弹：拆了要回牌堆、
 * 炸了进弃牌堆。两种都不该由这一层决定 —— 但它们有个共同点：
 * **这张牌不再属于"正在抽牌"这件事了**。所以 `release()` 只负责让它淡出。
 */

import { Container, type Renderer, type Ticker } from 'pixi.js';
import type { CardDef } from '../../core/card';
import type { CardFaceCache } from '../card';
import { CardFlip } from './cardFlip';
import { DURATION, easeOut } from '../ui/motion';

/**
 * 抽牌动画那张牌有多高（逻辑像素）。
 *
 * ⚠️ 它是**濒死层预渲染尺寸的来源**：`backdrop.ts` 拿它乘 `DYING_GROW_TO` 去烘
 *    辉光剪影。两边不一致的话，牌停在中点时会和它自己的光晕对不上。
 */
export const REVEAL_CARD_HEIGHT = 132;

/** 一个二维点。用结构类型，省得这一层 import 卡牌的几何模块 */
export interface Pt {
  readonly x: number;
  readonly y: number;
}

/**
 * **手牌给的落点姿态。**
 *
 * ⚠️ 它是"手牌已经为这张牌让出来的那一格"的样子，不是这里估出来的：
 * `Hand.reserveArrival()` 返回的就是它。
 */
export interface ArrivalPose extends Pt {
  /** 旋转（弧度）。手牌的 `roll` */
  readonly rotation: number;
  /** 尺寸倍率。手牌槽位现在都是 1，但接口留着 */
  readonly scale: number;
}

// ---------------------------------------------------------------- 调参 ----

/**
 * **每移动一个逻辑像素要多少毫秒。**
 *
 * 这就是"动画总时长根据移动距离线性增加"的那条直线 —— 一整段位移的时长
 * 是 `距离 × 它`。想整体调快/调慢，改这一个数。
 */
export const TRAVEL_MS_PER_PX = 0.55;

/**
 * 再近也要走这么久。
 *
 * ⚠️ 不能比 `2 × DURATION.fast` 小：① 的飞行时长同时是翻牌的时长，
 * 而翻牌两半各占一半 —— 短于 220ms 的话每一半就不足 110ms，
 * 掉出 `visual-design.md` §6 那条 90–220ms 的区间。
 */
export const MIN_TRAVEL_MS = DURATION.fast * 2;

/** 再远也不超过这么久。同样是为了翻牌那两半不超上限（`2 × DURATION.slow`） */
export const MAX_TRAVEL_MS = DURATION.slow * 2;

/**
 * **致命牌那一路的飞行时长系数。**
 *
 * ⚠️ 只乘在**致命牌**那一趟上（抽到异常牌 → 飞到画面正中），不是全局的
 *    `TRAVEL_MS_PER_PX` —— 普通抽牌的节奏是评审另外调过的，别动它。
 *
 * 评审：「在 IMPACT 之前的动画……参数都给的太小太保守，不够快不够炸裂」，
 * 所以这一趟比普通抽牌**快四分之一**：牌是被"甩到脸上"的。
 */
export const DYING_TRAVEL_SCALE = 0.7;

/**
 * **致命牌飞行的 ease-out 有多陡**（`t^n` 里的 n）。
 *
 * ⚠️ 普通抽牌用的是 `motion.ts` 的 `easeOut`（三次：t=0.5 时走了 87.5%）。
 *    这一趟换更陡的：末段更快贴到位、然后**硬停**在正中 —— 紧接着就是
 *    那一下吸气与打击。"甩"和"停"是同一件事的两半。
 */
export const DYING_FLY_POWER = 4;

/** 比 `motion.ts` 的 `easeOut` 更陡的 ease-out（**只在致命牌那一趟**上用） */
export function easeOutSharp(t: number, power: number = DYING_FLY_POWER): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  return 1 - (1 - k) ** power;
}

/**
 * ② 翻完之后停一下，让人看清抽到的是什么。
 *
 * ⚠️ 它同时是**命中前那一段"预备动作"**的时长（先缩再撑），
 *    而“打击”就落在它结束那一帧（`holding`）。评审："参数都给得太小
 *    太保守，不够快不够炸裂" —— 所以从 420 压到 320：预备更短、
 *    打击来得更快（而"吸气那一下"改由 `render/impact.ts` 的 `windupZoom` 叙事）。
 */
export const HOLD_MS = 220;

/**
 * **抽到致命牌时，飞行途中叠的那一下放大。**
 *
 * 评审：
 *
 * > 造成濒死的手牌在飞到屏幕中心的动画应叠加一个有非常夸张的 ease-in 速度
 * > 曲线的放大效果。
 *
 * 所以曲线不是普通的 ease-in，是 `t^4`：前三成时间几乎没动静，最后几帧猛地
 * 撑开 —— 那才是"扑到脸上"的观感。夸张的是**速度曲线**，而倍数本身也不小。
 *
 * ⚠️ **它是"撑到 2 倍并一直保持"，不是"弹一下再收回去"**（评审：
 *    「濒死期间我需要的是它维持 2 的尺寸，而不是 1.5 然后收回 1」）。
 *    所以停顿那一段、以及停在中点等抉择的整段时间里，它一直是 2 倍。
 *
 * ⚠️ **`held` 那一档也必须按这个尺寸摆** —— 踩过：只有飞行末段和 `hold` 用
 *    `DYING_GROW_TO`，而真正"停在中点等抉择"的 `held` 摆的是 1，
 *    于是肉眼看到的常态尺寸还是 1：评审的原话是
 *    「你未能将居中的异常牌常态放大，你只是使得尺寸倍率变为 3 了而已」。
 *
 * ⚠️ 后果是**辉光/阴影那些预渲染的东西必须按 2 倍烘**（它们围着一个固定尺寸的
 *    牌形剪影画），见 `render/game/backdrop.ts` 里给 `DyingLayer` 的 `card`。
 */
export const DYING_GROW_TO = 2;
/** ease-in 的夸张程度：`t^n` 里的 n。想更凶就加，想更平就减 */
export const DYING_GROW_POWER = 6;

/**
 * **到达中心之后先"缩一下"再撑开**（评审）：
 *
 * > 异常牌飞向中心期间的放大删去，改为飞向中心后稍微缩小（动画进度为负）再放大的
 * > ease-in 曲线最终达到 2 的尺寸。
 *
 * 所以停顿那一段（`hold`，`HOLD_MS`）分成两截：
 *
 * ```
 *   进度
 *    1 ┤                                    ╭──→ 2（最终尺寸）
 *      │                                 ╭──╯
 *    0 ┤──╮                          ╭───╯
 *      │   ╰──╮                  ╭────╯
 * -0.18┤       ╰──────────────────╯   ← 先缩到 0.82（"进度为负"）
 *      └────┬──────────────┬──────────────→ t
 *           0            0.35            1
 * ```
 *
 * 缩的那一下是**预备动作**（anticipation）：先往反方向走一点，再猛地撑开，
 * 撑开那一下才有力气。飞行途中**不再放大**（原来那一段是错的）。
 */
export const DYING_DIP_TO = 0.26;
/** 停顿那一段里，"缩"占多少比例（剩下的都留给撑开） */
export const DYING_DIP_SHARE = 0.3;

/**
 * **停顿那一段的尺寸进度。** 纯函数，`t` 是 `hold` 相位的进度 0..1。
 *
 * 返回的是"往 `DYING_GROW_TO` 走多远"的比例：`0` = 原尺寸、`1` = 撑满、
 * **负数 = 比原尺寸还小**。
 */
export function dyingPop(t: number, power: number = DYING_GROW_POWER): number {
  const k = clamp01(t);
  if (k <= DYING_DIP_SHARE) {
    // 缩：用 ease-out（先快后慢），免得开头那几帧看不出来。
    // ⚠️ 指数从 2 提到 3：评审要"更快"—— 同一个时长里缩得更狠、
    //    缩到位之后留给撑开的那一下就更突然
    const u = DYING_DIP_SHARE <= 0 ? 1 : k / DYING_DIP_SHARE;
    return -DYING_DIP_TO * (1 - (1 - u) ** 3);
  }
  const u = (k - DYING_DIP_SHARE) / (1 - DYING_DIP_SHARE);
  // 撑开：夸张的 ease-in（`t^4`），最后几帧猛地冲到位
  return -DYING_DIP_TO + (1 + DYING_DIP_TO) * easeInSharp(u, power);
}

/** 把"进度"换成真正的缩放倍率 */
export function dyingPopScale(progress: number): number {
  return 1 + (DYING_GROW_TO - 1) * progress;
}

/**
 * **非常夸张的 ease-in。** 纯函数，`t^n`。测试钉住它"前三成几乎不动"。
 *
 * ⚠️ 不要换成 `easeOut` 或普通 `easeIn`：这一下要的是"慢起、末段猛冲"，
 * 而普通的 `t^2` 在前三成已经走了 9%（`t^4` 是 0.8%）。
 */
export function easeInSharp(t: number, power: number = DYING_GROW_POWER): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  return k ** power;
}

/** 濒死结束后淡出 */
export const FADE_MS = 170;

/**
 * **一段位移要花多久。** 纯函数，所以"时长真的随距离线性增加"这件事能在
 * node 里钉住（`test/render/drawReveal.test.ts`）。
 *
 * 夹在 `[MIN_TRAVEL_MS, MAX_TRAVEL_MS]` 之间：太近的位移不该是瞬移，
 * 太远的位移不该慢到像卡住（而且翻牌那两半有硬区间）。
 */
export function travelMs(from: Pt, to: Pt): number {
  const distance = Math.hypot(to.x - from.x, to.y - from.y);
  const raw = distance * TRAVEL_MS_PER_PX;
  return Math.max(MIN_TRAVEL_MS, Math.min(MAX_TRAVEL_MS, raw));
}

/**
 * **角度插值走最短那一边。**
 *
 * ⚠️ 不能直接线插：手牌两端那一格的 `roll` 接近 ±0.5 弧度，而同一个方向
 * 可能算成 `0.54` 也可能算成 `-5.74`。直接插会让牌在空中转一整圈。
 */
export function rotationLerp(from: number, to: number, t: number): number {
  let delta = (to - from) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return from + delta * t;
}

// ---------------------------------------------------------------- 动画 ----

export type RevealPhase = 'fly' | 'hold' | 'settle' | 'held' | 'fade' | 'done';

export interface DrawRevealOptions {
  readonly renderer: Renderer;
  readonly def: CardDef;
  readonly height: number;
  readonly faces?: CardFaceCache;
  /** 起飞点：抽牌堆最上面那张牌背的中心 */
  readonly from: Pt;
  /** 中点：屏幕正中。翻牌在这里完成 */
  readonly center: Pt;
  /**
   * 落点：**手牌给的那一格**（坐标 + 旋转 + 尺寸）。
   *
   * `null` = **不飞进手牌**（抽到致命牌那种）—— 停在中点，等 `release()`。
   */
  readonly to: ArrivalPose | null;
  /**
   * 这一张是**造成濒死的那张**吗。
   *
   * 是的话飞行途中叠一下夸张的 ease-in 放大（`easeInSharp`），落地之后
   * 再收回去 —— 而濒死那一整套视觉（横带 / 辉光 / 裂缝）和 BGM 要等
   * **飞行结束**才生效（调用方按 `arrived` 放行，见 `ui/screens/game.ts`）。
   */
  readonly dying?: boolean;
}

export class DrawReveal {
  /** 加进特效层的东西 */
  readonly view: Container;

  readonly #flip: CardFlip;
  readonly #from: Pt;
  readonly #center: Pt;
  readonly #to: ArrivalPose | null;
  readonly #dying: boolean;
  readonly #half: { readonly w: number; readonly h: number };

  #phase: RevealPhase = 'fly';
  /** 当前这一段走了多久（毫秒） */
  #elapsed = 0;
  /** 当前这一段一共多久（毫秒）。相位一换就重算，见 `#enter` */
  #phaseMs = 0;
  #released = false;

  constructor(options: DrawRevealOptions) {
    this.#from = options.from;
    this.#center = options.center;
    this.#to = options.to;
    this.#dying = options.dying === true;
    this.#flip = new CardFlip({
      renderer: options.renderer,
      def: options.def,
      height: options.height,
      ...(options.faces === undefined ? {} : { faces: options.faces }),
      /**
       * ★ **濒死那张牌按放大后的尺寸画。**
       *
       * 评审：「牌面上的文字也存在尺寸放大后变糊的问题」。这一张会在落地后
       * 撑到 `DYING_GROW_TO` 倍并一直保持，所以牌面（含字形纹理）必须按
       * 那个尺寸烘，否则放大的是**一张已经烘好的小图**，笔画直接糊。
       *
       * 别的牌永远是 1 倍，超采样只会白白多占显存和绘制量 —— 所以只在
       * `dying` 这一条路上开。
       */
      supersample: options.dying === true ? DYING_GROW_TO : 1,
    });

    this.view = new Container();
    this.view.label = 'draw-reveal';
    this.view.eventMode = 'none';
    this.view.addChild(this.#flip.view);

    const size = this.#flip.size;
    this.#half = { w: size.width / 2, h: size.height / 2 };
    // ⚠️ 支点挪到**牌心**：旋转和缩放都该绕中心发生，绕左上角转会甩出去
    this.view.pivot.set(this.#half.w, this.#half.h);

    // ① 起飞，同时开翻。★ 两个动画**同时开始**，而且**一样长**
    const flyMs = travelMs(this.#from, this.#center);
    this.#phaseMs = flyMs;
    this.#place(this.#from, 0, 1);
    this.#flip.flip(flyMs);
  }

  get phase(): RevealPhase {
    return this.#phase;
  }

  /** 当前这一段的时长（毫秒）。**调参时最想看的那个数** */
  get phaseMs(): number {
    return this.#phaseMs;
  }

  /** 当前这一段走了多少毫秒。诊断用 */
  get elapsed(): number {
    return this.#elapsed;
  }

  /** 走完了没有。走完的由调用方销毁 */
  get finished(): boolean {
    return this.#phase === 'done';
  }

  /**
   * **飞到中点了没有。**
   *
   * ⚠️ 濒死那一整套东西（横带 / 辉光 / 裂缝 / BGM）等的就是这一刻 ——
   * 评审：「在飞到屏幕中心的动画完成后，所有濒死相关的视觉和 BGM 效果随即生效」。
   */
  get arrived(): boolean {
    return this.#phase !== 'fly';
  }

  /** 现在停在中点等濒死结束吗 */
  get holding(): boolean {
    return this.#phase === 'held';
  }

  /**
   * **濒死结束了**：这一张该走了。
   *
   * ⚠️ 只在 `held` 相位有效 —— 还在飞的那一段不该被"现在不濒死了"打断。
   */
  release(): void {
    if (this.#phase !== 'held') return;
    this.#released = true;
  }

  /**
   * 每帧推进。挂到**那唯一一个** ticker 回调上。
   *
   * ⚠️ 翻牌那一段**不在这里推** —— `CardFlip` 自己登记在 `ui/motion.ts` 的
   * 步进器上（`drive()`，宿主每帧推一次）。这里只推位置和时间轴。
   */
  update(ticker: Ticker): void {
    if (this.#phase === 'done') return;
    this.#elapsed += ticker.deltaMS;

    switch (this.#phase) {
      case 'fly': {
        const t = clamp01(this.#elapsed / this.#phaseMs);
        /**
         * ★ 直线 + ease-out：位置就是两点之间的一次插值。
         *
         * ⚠️ **飞行途中不放大**（评审：「异常牌飞向中心期间的放大删去」）——
         *    撑开是落地之后的事，见 `dyingPop`。
         */
        // ★ 致命牌那一趟用**更陡**的 ease-out（"甩到脸上"），普通抽牌照旧
        const k = this.#dying ? easeOutSharp(t) : easeOut(t);
        this.#place(lerpPt(this.#from, this.#center, k), 0, 1);
        if (t >= 1) this.#enter('hold');
        break;
      }
      case 'hold': {
        /**
         * ★ **落地之后：先缩一下，再 ease-in 撑到 `DYING_GROW_TO`。**
         *
         * 进度在中间是**负的**（比原尺寸还小一点）—— 见 `dyingPop`。
         */
        const t = clamp01(this.#elapsed / this.#phaseMs);
        this.#place(this.#center, 0, this.#dying ? dyingPopScale(dyingPop(t)) : 1);
        if (this.#elapsed >= this.#phaseMs) {
          // ★ 评审那条分岔就在这一行：抽到致命牌 → 停在正中等濒死结束
          this.#enter(this.#to === null ? 'held' : 'settle');
        }
        break;
      }
      case 'settle': {
        const to: ArrivalPose = this.#to ?? { ...this.#center, rotation: 0, scale: 1 };
        const t = clamp01(this.#elapsed / this.#phaseMs);
        const k = easeOut(t);
        this.#place(
          lerpPt(this.#center, to, k),
          rotationLerp(0, to.rotation, k),
          // 尺寸也补间（现在是 1 → 1，但接口留着）
          1 + (to.scale - 1) * k,
        );
        if (t >= 1) this.#enter('done');
        break;
      }
      case 'held': {
        /**
         * ★ **停在中点等抉择的整段时间里，一直是放大之后的尺寸。**
         *
         * ⚠️ 这里原来写的是 `1` —— 于是"撑到 2 倍"只在飞行末段闪一下，
         *    真正看得最久的那一段（停在中点）又缩回了原尺寸。
         *    字号是对的、倍率也是对的，唯独"常态"那一段漏了。
         */
        this.#place(this.#center, 0, this.#dying ? DYING_GROW_TO : 1);
        if (this.#released) this.#enter('fade');
        break;
      }
      case 'fade': {
        const t = clamp01(this.#elapsed / this.#phaseMs);
        this.view.alpha = 1 - t;
        if (t >= 1) this.#enter('done');
        break;
      }
    }
  }

  destroy(): void {
    // ⚠️ 顺序：先让翻牌那一层**自己退场**（它要把每帧队列上的登记摘掉，
    //    否则下一帧还会往已销毁的容器上写 `scale.x`），再销毁我这一层
    this.#flip.destroy();
    this.view.destroy({ children: true });
  }

  #enter(phase: RevealPhase): void {
    this.#phase = phase;
    this.#elapsed = 0;
    const base = PHASE_MS[phase](this.#from, this.#center, this.#to);
    /**
     * ★ **致命牌那一趟快四分之一**（只有 `fly` 那一段）。
     *
     * ⚠️ 普通抽牌的 `travelMs()` 一个字没动 —— 那个节奏评审另外调过。
     */
    this.#phaseMs = phase === 'fly' && this.#dying ? base * DYING_TRAVEL_SCALE : base;
  }

  /** 摆到"牌心在 p、转 `rotation`、缩 `scale`"（支点已经在牌心，见构造函数） */
  #place(p: Pt, rotation: number, scale: number): void {
    this.view.position.set(p.x, p.y);
    this.view.rotation = rotation;
    this.view.scale.set(scale);
  }
}

/** 每个相位多长。`held` 没有时长（等 `release()`），给 0 */
const PHASE_MS: Record<RevealPhase, (from: Pt, center: Pt, to: ArrivalPose | null) => number> = {
  fly: (from, center) => travelMs(from, center),
  hold: () => HOLD_MS,
  settle: (_from, center, to) => (to === null ? 0 : travelMs(center, to)),
  held: () => 0,
  fade: () => FADE_MS,
  done: () => 0,
};

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function lerpPt(a: Pt, b: Pt, t: number): Pt {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}
